/**
 * A simple store for sharing yaw values between PhotoSphereViewer and Compass
 * without causing re-renders of parent components.
 * 
 * This uses a subscription pattern where components can subscribe to yaw changes
 * and update their own state internally, avoiding prop drilling that causes re-renders.
 */

type YawListener = (yaw: number) => void;
const DEFAULT_YAW = -Math.PI/2;
class YawStore {
    private listeners: Set<YawListener> = new Set();
    private currentYaw: number = DEFAULT_YAW;

    subscribe(listener: YawListener): () => void {
        this.listeners.add(listener);
        // Immediately call with current value
        listener(this.currentYaw);
        
        // Return unsubscribe function
        return () => {
            this.listeners.delete(listener);
        };
    }

    setYaw(yaw: number): void {
        this.currentYaw = yaw;
        this.listeners.forEach(listener => listener(yaw));
    }

    getYaw(): number {
        return this.currentYaw;
    }

    reset(): void {
        this.currentYaw = DEFAULT_YAW;
        this.listeners.forEach(listener => listener(DEFAULT_YAW));
    }
}

// Singleton instance
export const yawStore = new YawStore();
